import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { HardHat, Plus, RotateCw, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const empty = { name: "", email: "", password: "" };

export default function Monteure() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [pwReset, setPwReset] = useState(null); // user obj
  const [pwValue, setPwValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users?role=monteur");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Laden");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name,
        role: "monteur",
      });
      toast.success("Monteur angelegt");
      setDialogOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anlegen fehlgeschlagen");
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!pwReset) return;
    try {
      await api.patch(`/users/${pwReset.id}/password`, { password: pwValue });
      toast.success("Passwort geändert");
      setPwReset(null);
      setPwValue("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Ändern");
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Monteur entfernt");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Entfernen");
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up" data-testid="monteure-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Link to="/einstellungen" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-3 w-3" /> Zurück zu Einstellungen
          </Link>
          <div className="overline">Verwaltung</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Monteure</h1>
          <p className="text-muted-foreground mt-1">Monteur-Konten für die Baustellen-App.</p>
        </div>
        <Button onClick={() => { setForm(empty); setDialogOpen(true); }} data-testid="new-monteur-button">
          <Plus className="h-4 w-4 mr-2" /> Neuer Monteur
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-12 text-center">
            <HardHat className="h-10 w-10 mx-auto text-muted-foreground" />
            <div className="font-heading text-xl font-semibold mt-4">Noch keine Monteure</div>
            <div className="text-sm text-muted-foreground mt-1" data-testid="no-monteure">
              Lege den ersten Monteur an — er kann sich dann in die App einloggen und sieht nur die zugewiesenen Projekte.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="monteure-list">
          {items.map((m) => (
            <Card key={m.id} className="border-border" data-testid={`monteur-${m.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-md bg-orange-500/10 flex items-center justify-center shrink-0">
                    <HardHat className="h-5 w-5 text-orange-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{m.name || m.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-border flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setPwReset(m); setPwValue(""); }} data-testid={`monteur-pw-${m.id}`} className="flex-1 h-7 text-[11px]">
                    <RotateCw className="h-3.5 w-3.5 mr-1" /> Passwort
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(m)} data-testid={`monteur-del-${m.id}`} className="h-7 text-[11px]">
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Neuer Monteur */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="monteur-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" /> Neuer Monteur
            </DialogTitle>
            <DialogDescription>
              Der Monteur kann sich mit E-Mail + Passwort einloggen und sieht nur ihm zugewiesene Projekt-Phasen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="m-name">Name *</Label>
              <Input id="m-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="m-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-email">E-Mail *</Label>
              <Input id="m-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="m-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-pw">Passwort * (mind. 6 Zeichen)</Label>
              <Input id="m-pw" type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="m-pw" placeholder="Wird dem Monteur mitgeteilt" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" data-testid="m-submit">Anlegen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Passwort zurücksetzen */}
      <Dialog open={!!pwReset} onOpenChange={(v) => { if (!v) { setPwReset(null); setPwValue(""); } }}>
        <DialogContent data-testid="m-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <RotateCw className="h-5 w-5 text-primary" /> Passwort ändern
            </DialogTitle>
            <DialogDescription>
              Neues Passwort für <span className="font-medium text-foreground">{pwReset?.email}</span>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Neues Passwort * (mind. 6 Zeichen)</Label>
              <Input type="text" minLength={6} value={pwValue} onChange={(e) => setPwValue(e.target.value)} required data-testid="m-pw-new" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setPwReset(null); setPwValue(""); }}>Abbrechen</Button>
              <Button type="submit" data-testid="m-pw-submit">Passwort ändern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Monteur entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{deleteTarget?.email}</span> kann sich danach nicht mehr einloggen. Zuweisungen zu Projekt-Phasen bleiben bestehen (nur der Login wird gelöscht).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="m-del-confirm">Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
